/* eslint-disable react-hooks/rules-of-hooks */

'use client';

import { useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { isCalculatorLoadingAtom } from '../../../Atoms/IsCalculatorLoading.atom';
import {
  saveCurrentCalculatorStateAtom,
  syncPersistedCalculatorStateAtom,
} from '../../../Atoms/PersistentCalculatorState.atom';
import { useCalculator } from './UseCalculator';

export const usePersistentCalculator = (vehicleId?: string) => {
  // Always call hooks at the top level regardless of conditions
  const calculatorResult = useCalculator(vehicleId || '');
  const syncCalculatorState = useSetAtom(syncPersistedCalculatorStateAtom);
  const saveCalculatorState = useSetAtom(saveCurrentCalculatorStateAtom);
  const setIsLoading = useSetAtom(isCalculatorLoadingAtom);

  useEffect(() => {
    if (!vehicleId) return;

    setIsLoading(true);

    try {
      syncCalculatorState();
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error syncing calculator state:', error);
      }
    }

    const timeout = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    const failSafeTimeout = setTimeout(() => {
      setIsLoading(false);
    }, 3000);

    return () => {
      clearTimeout(timeout);
      clearTimeout(failSafeTimeout);
    };
  }, [vehicleId, syncCalculatorState, setIsLoading]);

  useEffect(() => {
    if (!vehicleId) return;

    return () => {
      try {
        saveCalculatorState();
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error saving calculator state on unmount:', error);
        }
      }
    };
  }, [vehicleId, saveCalculatorState]);

  useEffect(() => {
    if (!vehicleId) return;

    const interval = setInterval(() => {
      try {
        saveCalculatorState();
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error in periodic calculator state save:', error);
        }
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [vehicleId, saveCalculatorState]);

  return typeof calculatorResult === 'object' && calculatorResult !== null ? calculatorResult : {};
};
